/**
 * Admin sub-page — who viewed which blot case, and when.
 *
 * Mounted as a tab inside /admin (see UserModuleAdmin.tsx). Reads from the
 * AccessLog table, populated by two existing flows: admin-shadow reads
 * (blot-analysis.js) and no-account guest reads via a shared-case link
 * (blot-case-guest-access.js — see GuestCaseAccess.tsx).
 */
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  HStack,
  Heading,
  Icon,
  Input,
  Link,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react'
import { formatDistanceToNow } from 'date-fns'
import { enUS, fr } from 'date-fns/locale'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'
import { LuEye, LuUserCheck, LuShieldCheck, LuFolderOpen, LuExternalLink } from 'react-icons/lu'
import { useCaseVisitors } from '../../api/api_hooks/admin/caseVisitors'
import { CHROME_HEADER_GRADIENT, CHROME_GLASS_BORDER } from '../../theme/chrome'

type TypeFilter = 'guest' | 'admin' | undefined

// Same "grow the limit" pattern as PilotDetailPanel's "Charger plus de cas"
// (PilotsAdmin.tsx) — the backend already does real server-side pagination
// (admin-case-visitors.js supports page/limit + returns totalItems/totalPages),
// this just re-fetches page 1 with a bigger limit each click rather than
// using true page numbers, so the list grows in place instead of paging.
const VISITORS_PAGE_SIZE = 50

export default function CaseVisitorsAdmin() {
  const { i18n } = useTranslation()
  const locale = i18n.language?.startsWith('fr') ? fr : enUS

  const [typeFilter, setTypeFilter] = useState<TypeFilter>(undefined)
  const [search, setSearch] = useState('')
  const [limit, setLimit] = useState(VISITORS_PAGE_SIZE)
  const { data, isLoading, isFetching } = useCaseVisitors({ search: search || undefined, type: typeFilter, limit })

  // A new filter/search narrows the result set — start back at one page of
  // it instead of carrying over however many rows were loaded before.
  const handleSearchChange = (value: string) => {
    setSearch(value)
    setLimit(VISITORS_PAGE_SIZE)
  }
  const handleTypeFilterChange = (value: TypeFilter) => {
    setTypeFilter(value)
    setLimit(VISITORS_PAGE_SIZE)
  }

  return (
    <VStack spacing={5} align="stretch">
      {/* ── Header ── */}
      <Card borderRadius="xl" borderWidth="1px" borderColor="gray.100" overflow="hidden">
        <Box bgGradient={CHROME_HEADER_GRADIENT} p={5} position="relative" overflow="hidden" sx={{ '&::after': CHROME_GLASS_BORDER }}>
          <HStack spacing={3}>
            <Box w="44px" h="44px" borderRadius="xl" bg="whiteAlpha.300" display="grid" sx={{ placeItems: 'center' }} backdropFilter="blur(8px)">
              <Icon as={LuEye} boxSize={5} color="white" />
            </Box>
            <VStack align="start" spacing={0}>
              <Heading size="md" color="white">Visiteurs de cas</Heading>
              <Text fontSize="xs" color="whiteAlpha.900">
                Qui a consulté quel cas, et quand — accès invité (sans compte) et accès admin
              </Text>
            </VStack>
          </HStack>
        </Box>

        <CardBody py={4}>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
            <StatPill label="Tous" value={data?.meta.totalItems ?? 0} active={!typeFilter} onClick={() => handleTypeFilterChange(undefined)} icon={LuFolderOpen} />
            <StatPill label="Invités (sans compte)" value={undefined} active={typeFilter === 'guest'} onClick={() => handleTypeFilterChange(typeFilter === 'guest' ? undefined : 'guest')} color="orange" icon={LuUserCheck} />
            <StatPill label="Admins" value={undefined} active={typeFilter === 'admin'} onClick={() => handleTypeFilterChange(typeFilter === 'admin' ? undefined : 'admin')} color="purple" icon={LuShieldCheck} />
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* ── Search ── */}
      <HStack>
        <Input
          placeholder="Rechercher par nom, email ou cas…"
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          bg="white"
          borderRadius="xl"
        />
      </HStack>

      {/* ── Table ── */}
      <Card borderRadius="xl" borderWidth="1px" borderColor="gray.200">
        <CardBody p={0}>
          {isLoading ? (
            <HStack p={8} justify="center"><Spinner /><Text color="gray.600">Chargement…</Text></HStack>
          ) : !data?.data.length ? (
            <VStack p={10} spacing={3}>
              <Icon as={LuEye} boxSize={8} color="gray.300" />
              <Text color="gray.600" fontWeight="600">Aucune consultation enregistrée</Text>
              <Text fontSize="sm" color="gray.500">Apparaît dès qu'un invité ouvre un lien de partage, ou qu'un admin consulte un cas qui n'est pas le sien.</Text>
            </VStack>
          ) : (
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>Visiteur</Th>
                    <Th>Type</Th>
                    <Th>Cas</Th>
                    <Th>Propriétaire</Th>
                    <Th>Consulté</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.data.map((entry) => (
                    <Tr key={entry.id} _hover={{ bg: 'gray.50' }}>
                      <Td>
                        <Text fontWeight="600" color="gray.800" fontSize="sm">{entry.visitor.name || '—'}</Text>
                        <Text fontSize="xs" color="gray.500">{entry.visitor.email || '—'}</Text>
                      </Td>
                      <Td>
                        {entry.visitor.isGuest ? (
                          <Badge colorScheme="orange" variant="subtle">
                            <HStack spacing={1}><Icon as={LuUserCheck} boxSize={3} /><Text>Invité</Text></HStack>
                          </Badge>
                        ) : (
                          <Badge colorScheme="purple" variant="subtle">
                            <HStack spacing={1}><Icon as={LuShieldCheck} boxSize={3} /><Text>Admin</Text></HStack>
                          </Badge>
                        )}
                      </Td>
                      <Td>
                        <Link
                          as={RouterLink}
                          to={`/lab/blot-analysis/${entry.case.id}`}
                          fontSize="sm"
                          color="teal.600"
                          fontWeight="600"
                          display="inline-flex"
                          alignItems="center"
                          gap={1}
                          _hover={{ textDecoration: 'underline' }}
                        >
                          {entry.case.label}
                          <Icon as={LuExternalLink} boxSize={3} />
                        </Link>
                        {entry.case.pathologyName && (
                          <Text fontSize="xs" color="gray.500">{entry.case.pathologyName}</Text>
                        )}
                      </Td>
                      <Td>
                        <Text fontSize="xs" color="gray.700">{entry.case.ownerName || '—'}</Text>
                      </Td>
                      <Td>
                        <Text fontSize="xs" color="gray.600">
                          Il y a {formatDistanceToNow(new Date(entry.createdAt), { locale })}
                        </Text>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
              {isFetching && (
                <HStack px={4} py={2} bg="gray.50" justify="center">
                  <Spinner size="xs" /><Text fontSize="xs" color="gray.500">Mise à jour…</Text>
                </HStack>
              )}
              {!isFetching && data && data.data.length < data.meta.totalItems && (
                <HStack px={4} py={3} justify="center" borderTop="1px solid" borderColor="gray.100">
                  <Button
                    size="sm"
                    variant="ghost"
                    colorScheme="teal"
                    onClick={() => setLimit((n) => n + VISITORS_PAGE_SIZE)}
                  >
                    Charger plus de visites ({data.data.length} / {data.meta.totalItems})
                  </Button>
                </HStack>
              )}
            </Box>
          )}
        </CardBody>
      </Card>
    </VStack>
  )
}

function StatPill({
  label, value, color = 'purple', icon, active, onClick,
}: {
  label: string
  value: number | undefined
  color?: string
  icon?: any
  active?: boolean
  onClick?: () => void
}) {
  return (
    <Box
      as="button"
      onClick={onClick}
      textAlign="left"
      p={3}
      borderRadius="xl"
      border="1px solid"
      borderColor={active ? `${color}.400` : 'gray.200'}
      bg={active ? `${color}.50` : 'white'}
      transition="all 0.2s"
      _hover={{ borderColor: `${color}.300`, transform: 'translateY(-1px)' }}
    >
      <HStack spacing={2} mb={1}>
        {icon && <Icon as={icon} color={`${color}.500`} boxSize={3.5} />}
        <Text fontSize="xs" color="gray.600" fontWeight="600">{label}</Text>
      </HStack>
      <Text fontSize="xl" fontWeight="800" color={`${color}.700`}>{value ?? '—'}</Text>
    </Box>
  )
}
